<template>
    <div
        :data-test-view="
            type === types.STRUCTURED
                ? 'feedback-create'
                : type === types.SELF_STRUCTURED
                ? 'feedback-self-reflection'
                : ''
        "
    >
        <!-- Select User -->
        <div v-if="!selectedUser" data-test-view="feedback-create-select-user">
            <div class="row">
                <div class="col-xs-12 text-center">
                    <img :src="imgNews" alt="Select User" width="150" />
                </div>
            </div>

            <l-space :size="2" />

            <l-input-typeahead
                :label="''"
                :data="users"
                :on-select="typeaheadUserOnSelect"
                :on-input-change="typeaheadUserOnInputChange"
                :placeholder="$t(`feedback.create.placeholderSelectUser`)"
                data-test-id="feedback-create-select-user"
            />

            <l-space :size="2" />

            <div class="footer">
                <button
                    class="btn btn-primary"
                    type="button"
                    data-test-id="feedback-create-close"
                    @click="$emit(`close`)"
                >
                    {{ $t(`feedback.create.btnClose`) }}
                </button>
            </div>
        </div>
        <!--/Select User -->

        <!-- Structured / Self-structued -->
        <div v-else-if="selectedUser && !showConfirmation" data-test-view="feedback-create">
            <!-- Intro -->
            <div class="row">
                <div class="col-md-3 text-center hidden-xs">
                    <l-profile-picture
                        :user="selectedUser"
                        size="l"
                        class="user-pic-feedback-panel"
                    />
                </div>

                <div class="col-md-9">
                    <!-- Custom Question -->
                    <div
                        v-show="requestTriggeredBy && customQuestion && type !== types.PRIVATE_NOTE"
                        style="margin-top: 10px"
                    >
                        <div class="hr-right-line">
                            <strong
                                >{{
                                    $t(`feedback.create.labelRequestMessage`, {
                                        firstname: selectedUser && selectedUser.firstname,
                                    })
                                }}
                            </strong>
                        </div>
                        <em style="word-break: break-word;">{{ customQuestion }}</em>
                        <hr />
                    </div>
                    <!--/Custom Question -->

                    <!-- Feedback Input -->
                    <l-input-text
                        ref="input-feedback"
                        v-model="content"
                        type="textarea"
                        :label="''"
                        :placeholder="$t(`feedback.create.placeholderGeneralComment`)"
                        :rows="4"
                        optional
                        hide-help-block
                        data-test-id="feedback-create-feedback-comment"
                    />
                    <!--/Feedback Input -->

                    <!-- Visibility -->
                    <span class="explanation color-grey">
                        <i class="fa fa-eye"></i>
                        <span class="l-default-margin-s">{{
                            $t(`feedback.create.labelVisibility`)
                        }}</span>

                        <span class="pull-right color-dark">
                            <!-- Sender Only -->
                            <span
                                v-show="[types.PRIVATE_NOTE, types.SELF_STRUCTURED].includes(type)"
                                v-b-popover.hover.top="{
                                    content: $t(`feedback.create.labelVisibilityOnlyYouHelp`),
                                }"
                                class="r-default-margin-s clickable"
                                data-test-id="feedback-create-visibility-senderOnly"
                                tabindex="0"
                                @click="setVisibility({ visibility: 'senderOnly' })"
                                @keyup.enter="setVisibility({ visibility: 'senderOnly' })"
                            >
                                <i
                                    :class="[
                                        visibility === 'senderOnly'
                                            ? 'fal fa-check-square'
                                            : 'fal fa-square',
                                    ]"
                                ></i>
                                <span class="l-default-margin-s">{{
                                    $t(`feedback.create.labelVisibilityOnlyYou`)
                                }}</span>
                            </span>
                            <!--/Sender Only -->

                            <!-- Receiver -->
                            <span
                                v-show="
                                    rights.F_V_RECEIVER &&
                                        type !== types.SELF_STRUCTURED &&
                                        type !== types.PRIVATE_NOTE
                                "
                                v-b-popover.hover.top="{
                                    content: $t(`feedback.create.labelVisibilityReceiverHelp`),
                                }"
                                class="r-default-margin-s l-default-margin-s clickable"
                                data-test-id="feedback-create-visibility-receiver"
                                tabindex="0"
                                @click="setVisibility({ visibility: 'receiver' })"
                                @keyup.enter="setVisibility({ visibility: 'receiver' })"
                            >
                                <i
                                    :class="[
                                        visibility === 'receiver'
                                            ? 'fal fa-check-square'
                                            : 'fal fa-square',
                                    ]"
                                ></i>
                                <span class="l-default-margin-s">{{
                                    $t(`feedback.create.labelVisibilityReceiver`, {
                                        firstname: selectedUser && selectedUser.firstname,
                                    })
                                }}</span>
                            </span>
                            <!--/Receiver -->

                            <!-- Receiver + Manager -->
                            <span
                                v-show="
                                    rights.F_V_RECEIVER_MANAGER &&
                                        type !== types.SELF_STRUCTURED &&
                                        type !== types.PRIVATE_NOTE
                                "
                                v-b-popover.hover.top="{
                                    content: $t(
                                        `feedback.create.labelVisibilityReceiverManagerHelp`,
                                    ),
                                }"
                                class="l-default-margin-s clickable"
                                data-test-id="feedback-create-visibility-receiverManager"
                                tabindex="0"
                                @click="setVisibility({ visibility: 'receiverManager' })"
                                @keyup.enter="setVisibility({ visibility: 'receiverManager' })"
                            >
                                <i
                                    :class="[
                                        visibility === 'receiverManager'
                                            ? 'fal fa-check-square'
                                            : 'fal fa-square',
                                    ]"
                                ></i>
                                <span class="l-default-margin-s">{{
                                    $t(`feedback.create.labelVisibilityReceiverManager`, {
                                        firstname: selectedUser && selectedUser.firstname,
                                    })
                                }}</span>
                            </span>
                            <!--/Receiver + Manager -->
                        </span>
                    </span>
                    <!--/Visibility -->

                    <!-- Feedback Signals -->
                    <div v-show="rights.F_STRUCTURED">
                        <l-space :size="1" />

                        <!-- Signals Selected -->
                        <div v-for="signal in signals.filter(s => s.selected)" :key="signal._id">
                            <l-space :size="1" />

                            <!-- Signal Headline -->
                            <div>
                                <strong>{{ signal.skillname }}</strong>
                                <span
                                    v-show="signal.requested"
                                    class="color-grey explanation l-default-margin-s"
                                >
                                    <i class="fa fa-refresh"></i>
                                    <span class="l-default-margin-s">{{
                                        $t(`feedback.create.labelRequested`)
                                    }}</span>
                                </span>
                                <span
                                    v-show="signal.skilldescription"
                                    class="color-primary explanation l-default-margin-s"
                                    tabindex="0"
                                    @click="signal.showDescription = !signal.showDescription"
                                    @keyup.enter="signal.showDescription = !signal.showDescription"
                                >
                                    <i class="fa fa-info"></i>
                                </span>
                                <span
                                    class="explanation color-primary l-default-margin-s"
                                    tabindex="0"
                                    @click="removeSkill({ signal })"
                                    @keyup.enter="removeSkill({ signal })"
                                >
                                    <i class="fa fa-remove"></i>
                                </span>
                                <span class="explanation">
                                    &nbsp;
                                    <!-- prevents line from jumping -->
                                </span>
                            </div>
                            <!--/Signal Headline -->

                            <!-- Signal Description -->
                            <div v-show="signal.showDescription">
                                <l-text :text="signal.skilldescription" />
                                <l-space :size="1" />
                            </div>
                            <!--/Signal Description -->

                            <!-- Signal Volume Bar -->
                            <div class="volumeRow">
                                <span
                                    v-b-popover.hover.bottom="{
                                        content: $t(`feedback.create.labelNeedsImprovement`),
                                    }"
                                    style="margin-top: 5px; margin-right: 30px"
                                    tabindex="0"
                                    @click="signal.amount = 0"
                                    @keyup.enter="signal.amount = 0"
                                >
                                    <span style="font-size: 21px;">
                                        <i class="fal fa-rocket color-warning"></i>
                                    </span>
                                </span>

                                <span
                                    class="volumeBar"
                                    data-test-class="feedback-create-skill-volume-bar"
                                    @mousedown="e => sliderMouseDown(e, signal)"
                                    @mouseup="e => sliderMouseUp(e, signal)"
                                    @mousemove="e => sliderMouseMove(e, signal)"
                                >
                                    <span class="volumebkg"></span>
                                    <span
                                        ref="volume"
                                        class="volume"
                                        :style="sliderStyle({ percentage: signal.amount })"
                                    ></span>
                                </span>

                                <span
                                    style="margin-left: 35px;"
                                    tabindex="0"
                                    @click="signal.amount = 100"
                                    @keyup.enter="signal.amount = 100"
                                >
                                    <span style="font-size: 21px">
                                        <i class="fal fa-rocket color-success"></i>
                                    </span>
                                </span>
                            </div>
                            <!--/Signal Volume Bar -->

                            <!-- Signal Explanation -->
                            <div class="row">
                                <div class="col-md-6">
                                    <span class="explanation">{{
                                        $t(`feedback.create.labelNeedsImprovement`)
                                    }}</span>
                                </div>
                                <div class="col-md-6">
                                    <span class="explanation pull-right">{{
                                        $t(`feedback.create.labelAboveExpectations`)
                                    }}</span>
                                </div>
                            </div>
                            <!--/Signal Explanation -->

                            <!-- Signal Feedback -->
                            <div>
                                <l-input-text
                                    v-model="signal.content"
                                    type="textarea"
                                    :label="''"
                                    :placeholder="$t(`feedback.create.placeholderOptionalComment`)"
                                    :optional="true"
                                    :hide-help-block="true"
                                    :rows="1"
                                    data-test-class="feedback-create-skill-comment"
                                />
                                <p
                                    v-show="
                                        signal.amount > 0 &&
                                            signal.amount < 85 &&
                                            signal.content == ''
                                    "
                                    class="explanation text-center explanation"
                                >
                                    <i class="fal fa-comment"></i>
                                    <span class="l-default-margin-s">{{
                                        $t(`feedback.create.labelProvideExamples`)
                                    }}</span>
                                </p>
                            </div>
                            <!--/Signal Feedback -->

                            <l-space :size="1" />
                        </div>
                        <!--/Signals Selected -->

                        <l-space :size="1" />

                        <!-- Select Skills for feedback -->
                        <div
                            v-for="signal in signals.slice(0, signalCountLimit)"
                            :key="`${signal._id}__2`"
                        >
                            <div
                                v-show="!signal.selected"
                                class="hr-right-line"
                                data-test-class="feedback-create-skills-add"
                                tabindex="0"
                                @click="addSkill({ signal })"
                                @keyup.enter="addSkill({ signal })"
                            >
                                <i class="fa fa-plus color-primary"></i>
                                <span class="l-default-margin-s">
                                    {{ signal.skillname }}
                                </span>
                            </div>
                            <div v-show="signal.selected" class="hr-right-line">
                                <i class="fa fa-check"></i>
                                <span class="l-default-margin-s">
                                    {{ signal.skillname }}
                                </span>
                            </div>
                        </div>

                        <div
                            v-show="signalCountLimit < signals.length + 1"
                            class="hr-right-line"
                            tabindex="0"
                            @click="signalCountLimit = signals.length + 1"
                            @keyup.enter="signalCountLimit = signals.length + 1"
                        >
                            <i class="fa fa-angle-double-down color-primary"></i>
                            <span class="l-default-margin-s">{{
                                $t(`feedback.create.btnShowAllSkills`)
                            }}</span>
                        </div>
                        <!--/Select Skills for feedback -->
                    </div>
                    <!--/Feedback Signals -->
                </div>
            </div>

            <div class="footer">
                <button
                    class="btn btn-primary"
                    type="button"
                    data-test-id="feedback-create-close"
                    @click="$emit(`close`)"
                >
                    {{ $t(`feedback.create.btnClose`) }}
                </button>
                <button
                    class="btn btn-raised btn-primary"
                    type="button"
                    data-test-id="feedback-create-send"
                    :disabled="isLoading"
                    @click="sendFeedback()"
                >
                    {{
                        type === types.PRIVATE_NOTE
                            ? $t(`feedback.create.btnSave`)
                            : $t(`feedback.create.btnSend`)
                    }}
                </button>
            </div>
        </div>

        <!-- Confirmation -->
        <div v-else-if="showConfirmation" data-test-view="feedback-create-confirmation">
            <div class="row">
                <div class="col-xs-12 text-center">
                    <img :src="imgNews" alt="Feedback Sent" width="150" />
                    <h3>
                        <strong>{{ $t(`feedback.create.titleConfirmation`) }} </strong>
                    </h3>
                    <h4>{{ $t(`feedback.create.titleConfirmationSub`) }}</h4>
                </div>
            </div>

            <l-space :size="1" />

            <div class="footer">
                <button
                    class="btn btn-primary"
                    type="button"
                    data-test-id="feedback-create-close"
                    @click="$emit(`close`)"
                >
                    {{ $t(`feedback.create.btnClose`) }}
                </button>
            </div>
        </div>
        <!--/Confirmation -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import format from "@/utils/format-typeahead";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LProfilePicture from "@/components/LProfilePicture";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";

import imgNews from "@/img/icons/news.png";

import types from "@/config/feedback-types";

export default {
    components: {
        LInputText,
        LInputTypeahead,
        LProfilePicture,
        LSpace,
        LText,
    },
    data() {
        return {
            types: Object.freeze(types),

            selectedUser: null,
            type: this.$route.query?.type ?? types.STRUCTURED,
            visibility: "receiver",

            showConfirmation: false,

            content: "",
            signals: [],
            read: false,

            volumeDrag: false,
            signalCountLimit: 10,

            isLoading: false,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            rights: "users/current/rights",
            getRequestDetails: "feedback/request/details",
            getReceiverDetails: "users/details/details",
            usersSearchList: "users/search/list",
            getUserList: "users/list/list",
            getSkillsByUser: "skills/getSkillsByUser",
        }),
        users() {
            let list = this.usersSearchList;
            if (!list.length) {
                list = this.getUserList({ key: "directTeam" });
            }

            if (this.type === this.types.PRIVATE_NOTE) {
                // one cannot add private notes to oneself, instead the feedback
                // form is used for a "self reflection"
                list = list?.filter(user => user._id !== this.user?._id);
            }

            return list.map(format.users);
        },

        // From Request
        requestTriggeredBy() {
            return this.$route.query.requestedBy;
        },
        request() {
            if (this.requestTriggeredBy) {
                return this.getRequestDetails(this.requestTriggeredBy);
            }
            return null;
        },
        customQuestion() {
            return this.request?.parameters?.requestQuestion;
        },
    },
    watch: {
        selectedUser(newValue) {
            if (!newValue) return;

            // adjust the title eventually
            if (this.type === this.types.PRIVATE_NOTE) {
                this.$emit(
                    "title",
                    this.$t(`feedback.create.titlePrivateNote`, {
                        firstname: newValue.firstname,
                    }),
                    true, // Show private note lock
                );
            } else {
                this.$emit("title", this.$t(`feedback.create.titleGiveFeedback`));
            }

            // Show relevant skill selection
            if (this.selectedUser?._id) this.signals = this.parseSkills();

            if (this.request) {
                // Mark as selected and requested the signals selected in the request
                this.request.parameters?.requestSkills?.forEach(skill => {
                    const signal = this.signals.find(s => s.skill === skill);

                    if (signal) {
                        signal.selected = true;
                        signal.requested = true;
                    }
                });
            }
        },
    },
    created() {
        this.imgNews = imgNews;

        // Selected User (from query)
        if (this.$route.query?.to) {
            this.selectedUser = this.getReceiverDetails(this.$route.query.to);
        } else if (this.request) {
            // Note that the receiver of the feedback is the sender of the request
            this.selectedUser = this.request?.sender?.user;
        }

        // Type
        if (this.type === this.types.PRIVATE_NOTE) {
            this.setVisibility({ visibility: "senderOnly" });
        }
    },
    methods: {
        parseSkills() {
            return this.getSkillsByUser(this.selectedUser._id).map(skill => ({
                content: "",
                amount: -100,
                weight: skill.weight,
                skillAnswerType: skill.answerType,
                label: skill.label,
                status: skill.status,
                keyResults: skill.keyResults,
                _id: skill._id,
                skill: skill._id,
                skillname: skill.name,
                skilltype: skill.type,
                skilldescription:
                    skill.descriptionCustomRoles &&
                    skill.customRole &&
                    skill.descriptionCustomRoles[skill.customRole] &&
                    skill.descriptionCustomRoles[skill.customRole].main
                        ? skill.descriptionCustomRoles[skill.customRole].main
                        : skill.description,
                skillcategory: skill.category,
                progress: skill.progress,
                ratingRequirement: skill.ratingRequirement,
                commentRequirement: skill.commentRequirement,
                type: this.type,
                applicableToRole: skill.applicableToRole,
                inputRequired: skill.inputRequired,
                role: skill.role ? skill.role : "all",
                customRole: skill.customRole,
                descriptionCustomRoles: skill.descriptionCustomRoles,
                showDescription: false,
                selected: false,
            }));
        },
        typeaheadUserOnSelect(user) {
            this.selectedUser = user;

            // private notes keep their default
            if (this.type !== this.types.PRIVATE_NOTE) {
                // If selected user is current user, this is a self reflection
                // Update type and title
                if (this.selectedUser._id === this.user._id) {
                    this.type = this.types.SELF_STRUCTURED;
                    this.visibility = "senderOnly";
                    this.read = true;
                    this.$emit("title", this.$t(`feedback.create.titleSelfReflection`));
                } else if (this.rights.F_V_RECEIVER) {
                    this.visibility = "receiver";
                } else {
                    this.visibility = "receiverManager";
                }
            }

            // Fetch relevant skills for selected user
            this.$store
                .dispatch({
                    type: "skills/getListForUser",
                    payload: { userId: this.selectedUser._id },
                })
                .then(() => {
                    this.signals = this.parseSkills();
                });
        },
        typeaheadUserOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },

        setVisibility({ visibility }) {
            if (this.selectedUser?._id === this.user._id) {
                return;
            }

            if (visibility === "senderOnly") {
                this.type = this.types.PRIVATE_NOTE;
                this.visibility = "senderOnly";
                this.read = true;

                if (!this.selectedUser) return;

                this.$emit(
                    "title",
                    this.$t(`feedback.create.titlePrivateNote`, {
                        firstname: this.selectedUser.firstname,
                    }),
                    true, // Show private note lock
                );

                return;
            }

            if (visibility === "receiver") {
                this.type = this.types.STRUCTURED;
                this.visibility = "receiver";
                this.read = false;

                this.$emit("title", this.$t(`feedback.create.titleGiveFeedback`));
                return;
            }

            if (visibility === "receiverManager") {
                this.type = this.types.STRUCTURED;
                this.visibility = "receiverManager";
                this.read = false;

                this.$emit("title", this.$t(`feedback.create.titleGiveFeedback`));
                return;
            }
        },

        addSkill({ signal }) {
            const signalIndex = this.signals.findIndex(s => s.skill === signal.skill);
            this.signals[signalIndex].selected = true;
        },
        removeSkill({ signal }) {
            const signalIndex = this.signals.findIndex(s => s.skill === signal.skill);
            this.signals[signalIndex].selected = false;
            this.signals[signalIndex].amount = -100;
        },

        sliderMouseDown(e, signal) {
            this.volumeDrag = true;
            this.updateVolume(e.clientX, signal);
        },

        sliderMouseUp(e, signal) {
            this.volumeDrag = false;
            this.updateVolume(e.clientX, signal);
        },

        sliderMouseMove(e, signal) {
            if (this.volumeDrag) {
                this.updateVolume(e.clientX, signal);
            }
        },

        sliderStyle({ percentage }) {
            return {
                clip: "rect(0px, " + percentage * 2.2 + "px, 30px, 0px)",
                "border-color":
                    percentage <= 55
                        ? "transparent transparent #ffb54a transparent"
                        : "transparent transparent #5D5DE9 transparent",
            };
        },

        updateVolume(x, signal) {
            const volume = this.$refs["volume"][0];
            const position = x - volume.getBoundingClientRect().x;

            const percentage = Math.min(
                100,
                Math.max(0, Math.round((position / volume.offsetWidth) * 100)),
            );

            const signalIndex = this.signals.findIndex(s => s.skill === signal.skill);
            this.signals[signalIndex].amount = percentage;
        },

        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },

        sendFeedback() {
            if (!this.isValid()) {
                return;
            }

            this.isLoading = true;

            const signals = this.signals;

            // Prepend the `content` as a feedback signal
            signals.unshift({
                amount: -100,
                content: this.content,
                visibility: this.visibility,
            });

            this.$store
                .dispatch({
                    type: "feedback/feedback/create",
                    payload: {
                        type: this.type,
                        visibility: this.visibility,
                        customQuestion: this.customQuestion,
                        signals,
                        receiver: this.selectedUser._id,
                        read: this.read,
                    },
                })
                .then(() => {
                    this.showConfirmation = true;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style scoped></style>
